import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Die Seite zur Weihnachtsfeier.
 *
 * 1.180 Impressionen in 90 Tagen und kein einziger Klick. Sie lief über
 * dieselbe Vorlage wie die Stadtseiten.
 *
 * Bewusst kein zweiter Aufguss der Firmenfeier-Seite: Was für jede
 * Firmenfeier gilt — Logo im Layout, Rechnung auf die Firma, Datenschutz —
 * steht dort und wird von hier verlinkt. Hier steht nur, was den Dezember
 * anders macht.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-6 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function WeihnachtsfeierInhalt({ analyse, stand }) {
  if (!analyse) return null;

  return (
    <div className="space-y-12 lg:space-y-16">
      <Abschnitt
        id="wann-buchen"
        titel="Warum der Dezember früher ausgebucht ist als jeder andere Monat"
        untertitel="Alle wollen dieselben drei Wochenenden."
      >
        <p>
          Eine Hochzeit im Juni verteilt sich über den ganzen Sommer. Eine
          Weihnachtsfeier liegt fast immer zwischen dem ersten und dritten
          Adventswochenende — und die meisten Firmen wählen einen Freitag. Das
          heißt: Wenige Termine, viele Anfragen, und wer Ende November sucht,
          bekommt oft nur noch, was übrig ist.
        </p>
        <p className="mt-4">
          <strong className="font-semibold text-slate-900">
            Bucht im September oder Oktober.
          </strong>{" "}
          Zu diesem Zeitpunkt ist die Auswahl vollständig, und ihr könnt nach
          der Box entscheiden, nicht nach der Verfügbarkeit.
        </p>
        {analyse.perVersand > 0 && (
          <p className="mt-4">
            Für den Versand gilt zusätzlich: Im Dezember fahren dieselben
            Paketdienste wie sonst — nur mit deutlich mehr Sendungen. Plant
            beim Liefertermin lieber zwei Tage mehr ein.{" "}
            {analyse.perVersand} der {analyse.anzahlBoxen} passenden Boxen
            kommen per Paket.
          </p>
        )}
      </Abschnitt>

      <Abschnitt
        id="druck"
        titel="Wie viel gedruckt wird, wenn die Kolleginnen und Kollegen loslegen"
        untertitel="Die Rechnung, die den Abend rettet."
      >
        <p>
          Für {analyse.gaeste} Gäste solltet ihr mit{" "}
          <strong className="font-semibold text-slate-900">
            rund {analyse.bedarf} Ausdrucken
          </strong>{" "}
          rechnen — zwei bis drei pro Person. Auf einer Weihnachtsfeier eher
          mehr als weniger: Es wird in Abteilungen fotografiert, und wer im
          Bild ist, will einen Ausdruck.
        </p>
        <p className="mt-4">
          {analyse.anzahlPakete} Pakete schaffen das, ab{" "}
          {euro(analyse.min)} €. Wenn ihr nicht rechnen wollt, nehmt eine
          Druck-Flatrate — sie kostet oft weniger als ein großes Kontingent und
          nimmt euch die Frage ab, wie viel Papier noch da ist.
        </p>
      </Abschnitt>

      <Abschnitt
        id="praktisch"
        titel="Drei Dinge, die im Dezember dazukommen"
        untertitel="Was bei einer Sommerfeier keine Rolle spielt."
      >
        <ul className="list-disc space-y-2.5 pl-5 marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">
              Die Box kommt aus der Kälte.
            </strong>{" "}
            Wird sie aus einem kalten Transporter direkt in den warmen Saal
            gestellt, kann Glas beschlagen. Gebt ihr eine halbe Stunde Zeit,
            bevor die ersten Gäste da sind — die Anbieter wissen das, aber es
            schadet nicht, den Aufbau entsprechend früh zu legen.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Der Raum ist meist voller als gedacht.
            </strong>{" "}
            Weihnachtsfeiern finden drinnen statt, oft in Räumen, die für das
            Essen ausgelegt sind. Klärt vorher, wo die Box steht und ob davor
            genug Platz für eine Gruppe bleibt.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Das Layout darf weihnachtlich sein.
            </strong>{" "}
            Die meisten Anbieter drucken auf Wunsch mit eurem Logo und dem
            Anlass — „Weihnachtsfeier 2026" auf dem Streifen macht aus dem
            Ausdruck ein Andenken. Schickt die Vorlage einige Tage vorher.
          </li>
        </ul>
        <p className="mt-6">
          Alles Weitere zur Firmenfeier — Logo im Drucklayout, Rechnung auf die
          Firma und was beim Datenschutz zu klären ist — steht auf der{" "}
          <Link
            href="/fotobox-mieten-firmenfeier"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Seite zur Fotobox für Firmenfeiern
          </Link>
          .
        </p>
        <p className="mt-6 text-xs text-slate-500">
          Alle Zahlen aus dem Katalog, Stand {stand}.
        </p>
      </Abschnitt>
    </div>
  );
}
